import React, { createContext, useContext, useEffect, useRef, useCallback } from 'react';
import { detectEventType, getAlarmConfig } from '../utils/alarmConfig';
import { useStatus } from '../popup/NotificationContext';
import { allSounds } from '../assets/sounds';
import { isAppSoundEnabled } from '../utils/appSound';

// 🔥 COMPLETE ALARM SOUND MAP
const ALARM_SOUNDS = {
  // 🚨 CRITICAL/EMERGENCY
  sos_alarm: allSounds.sosAlarm,
  sos: allSounds.sosAlarm,
  panic: allSounds.sosAlarm,
  panic_button: allSounds.sosAlarm,
  tamper: allSounds.sosAlarm,
  tampering: allSounds.sosAlarm,
  collision: allSounds.sosAlarm,
  collision_alarm: allSounds.sosAlarm,
  rollover_alarm: allSounds.sosAlarm,
  
  // 🚛 TOWING
  tow: allSounds.towingAlarm,
  towing_alarm: allSounds.towingAlarm,
  unauthorized_movement: allSounds.towingAlarm,
  
  // ⚡ SPEED/HARSH DRIVING
  overspeed: allSounds.overspeed,
  overspeed_alarm: allSounds.overspeed,
  speeding: allSounds.overspeed,
  harsh_braking: allSounds.harshBraking,
  harsh_acceleration: allSounds.harshAcceleration,
  harsh_cornering: allSounds.harshBraking,
  
  // 🔑 ENGINE/IGNITION
  ignition_on: allSounds.engineStart,
  ignition_off: allSounds.engineStop,
  acc_on: allSounds.engineStart,
  acc_off: allSounds.engineStop,
  engine_start: allSounds.engineStart,
  engine_stop: allSounds.engineStop,
  
  // 📍 GEOFENCE
  geofence_entry: allSounds.geofenceEntry,
  geofence_exit: allSounds.geofenceExit,
  geofence_violation: allSounds.sosAlarm,
  enter_area: allSounds.geofenceEntry,
  exit_area: allSounds.geofenceExit,
  
  // 🔋 POWER/BATTERY
  low_battery: allSounds.lowBattery,
  battery_low: allSounds.lowBattery,
  power_cut: allSounds.powerOff,
  power_off: allSounds.powerOff,
  external_power_cut: allSounds.powerOff,
  power_on: allSounds.powerOn,
  power_restored: allSounds.powerOn,
  
  // 📡 CONNECTION
  device_connected: allSounds.deviceConnected,
  device_disconnected: allSounds.deviceDisconnected,
  device_offline: allSounds.deviceDisconnected,
  
  // 🛢️ FUEL
  fuel_theft: allSounds.fuelTheft,
  fuel_drop: allSounds.fuelTheft,
  
  // 📶 GPS
  gps_signal_lost: allSounds.gpsLost,
  gps_antenna_disconnected: allSounds.gpsLost,
  gps_antenna_restored: allSounds.gpsRestored,
  
  // ⏱️ IDLE
  idle_too_long: allSounds.idleTooLong,
  low_speed_alarm: allSounds.idleTooLong,
  
  // 📳 VIBRATION
  vibration_alarm: allSounds.sosAlarm,
  
  // ✅ SUCCESS/INFO
  moving: allSounds.engineStart,
  stopped: allSounds.engineStop,
  idle: allSounds.idleTooLong,
};

const AlarmContext = createContext();

export const AlarmProvider = ({ children }) => {
  const { showStatus } = useStatus();
  const audioRef = useRef(null);
  const eventQueue = useRef([]);
  const isProcessingQueue = useRef(false);
  
  const lastAlarmRef = useRef({});
  const processedEventsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);
  const lastProcessedTimeRef = useRef(Date.now());

  // 🎵 FIXED SOUND PLAYER
  const playSound = useCallback(async (alarmType, volume = 0.7) => {
    if (!isAppSoundEnabled()) return;

    return new Promise((resolve) => {
      try {
        // 🎯 Direct mapping
        const soundFile = ALARM_SOUNDS[alarmType] || allSounds.sosAlarm;
        
        console.log('🔊 Playing sound:', alarmType, '→', soundFile);
        
        const audio = new Audio(soundFile);
        
        // Critical alarms louder
        const isCritical = ['sos', 'sos_alarm', 'panic', 'collision'].includes(alarmType);
        audio.volume = isCritical ? 0.9 : volume;
        
        audioRef.current = audio;

        // Critical alarms loop 3 times
        if (isCritical) {
          let loops = 0;
          audio.onended = () => {
            loops++;
            if (loops < 3) audio.play().catch(() => resolve());
            else resolve();
          };
        } else {
          audio.onended = () => resolve();
        }

        audio.play().catch((err) => {
          console.warn('Audio blocked:', err);
          resolve(); 
        });
      } catch (err) {
        console.error('Audio Error:', err);
        resolve();
      }
    });
  }, []);

  // Queue Processor
  const processNextInQueue = useCallback(async () => {
    if (isProcessingQueue.current || eventQueue.current.length === 0) return;
    
    isProcessingQueue.current = true;
    const { event, config, message, statusType } = eventQueue.current.shift();

    showStatus(statusType, message);

    if ('vibrate' in navigator) navigator.vibrate(config.vibrate);
    
    // 🎯 Pass alarm type directly
    await playSound(event.eventType || event.alarmType);

    isProcessingQueue.current = false;
    processNextInQueue();
  }, [playSound, showStatus]);

  // Message Formatter
  const getMessage = useCallback((type, name, data) => {
    const icons = { 
      sos: '🚨', 
      overspeed: '⚡', 
      ignition_on: '🔑',
      engine_start: '🔑',
      power_cut: '🔋',
      device_offline: '📵',
      geofence_entry: '📍',
      geofence_exit: '🚪',
      default: '📢' 
    };
    const icon = icons[type] || icons.default;
    
    let extra = '';
    if (type === 'overspeed' && data?.speed) extra = ` (${data.speed} km/h)`;
    if (type === 'low_battery' && data?.battery) extra = ` (${data.battery}%)`;

    return `${icon} ${type.replace(/_/g, ' ').toUpperCase()}: ${name}${extra}`;
  }, []);

  const triggerAlarm = useCallback((event, deviceName = 'Unknown') => {
    const eventType = detectEventType(event);
    if (!eventType) return false;

    const config = getAlarmConfig(eventType);
    const alarmKey = `${event.imei}-${eventType}-${event.timestamp}`;
    
    // Duplicate prevention
    const now = Date.now();
    if (lastAlarmRef.current[alarmKey] && (now - lastAlarmRef.current[alarmKey]) < 10000) {
      return false;
    }
    lastAlarmRef.current[alarmKey] = now;

    const statusType = config.priority === 'critical' ? 'error' : 
                      config.priority === 'high' ? 'warning' : 'info';

    // Queue alarm
    eventQueue.current.push({
      event: { ...event, eventType }, // ✅ Store eventType
      config,
      statusType,
      message: getMessage(eventType, deviceName, event)
    });

    processNextInQueue();
    return true;
  }, [getMessage, processNextInQueue]);

  // Process Events
  const processEvents = useCallback((devices) => {
    if (!devices || devices.length === 0) return;

    if (isFirstLoadRef.current) {
      devices.forEach(d => (d.events || []).forEach(e => 
        processedEventsRef.current.add(`${d.imei}-${e.eventType}-${e.timestamp}`)
      ));
      isFirstLoadRef.current = false;
      return;
    }

    const now = Date.now();
    const cutoff = Math.max(now - 30000, lastProcessedTimeRef.current);

    devices.forEach(device => {
      if (!device.events) return;
      
      device.events.forEach(event => {
        const eventTime = new Date(event.timestamp).getTime();
        const key = `${device.imei}-${event.eventType}-${event.timestamp}`;

        if (eventTime > cutoff && !processedEventsRef.current.has(key)) {
          triggerAlarm({ ...event, imei: device.imei }, device.name || device.deviceName);
          processedEventsRef.current.add(key);
        }
      });
    });

    lastProcessedTimeRef.current = now;

    if (processedEventsRef.current.size > 500) {
      const kept = Array.from(processedEventsRef.current).slice(-200);
      processedEventsRef.current = new Set(kept);
    }
  }, [triggerAlarm]);

  return (
    <AlarmContext.Provider value={{ triggerAlarm, processEvents, manualAlarm: triggerAlarm }}>
      {children}
    </AlarmContext.Provider>
  );
};

export const useAlarm = () => useContext(AlarmContext);
export default AlarmProvider;
